import type React from "react";
import { COUNTRY_LIST, type Country } from "../constants/countries";

interface PersonalDetailsSectionProps {
	firstName: string;
	lastName: string;
	email: string;
	country: Country;
	onFirstNameChange: (value: string) => void;
	onLastNameChange: (value: string) => void;
	onEmailChange: (value: string) => void;
	onCountryChange: (value: Country) => void;
	// Validation props
	getFieldError: (fieldName: string) => string | null;
	hasFieldError: (fieldName: string) => boolean;
	touchField: (fieldName: string) => void;
	validateSingleField: (fieldName: string, value: string) => void;
}

export const PersonalDetailsSection: React.FC<PersonalDetailsSectionProps> = ({
	firstName,
	lastName,
	email,
	country,
	onFirstNameChange,
	onLastNameChange,
	onEmailChange,
	onCountryChange,
	getFieldError,
	hasFieldError,
	touchField,
	validateSingleField,
}) => {
	const handleFieldChange = (
		fieldName: string,
		value: string,
		onChange: (value: string) => void,
	) => {
		onChange(value);
		validateSingleField(fieldName, value);
	};

	const handleCountryChange = (value: Country) => {
		onCountryChange(value);
		validateSingleField("country", value);
	};

	const handleFieldBlur = (fieldName: string) => {
		touchField(fieldName);
	};

	const getInputClassName = (fieldName: string, baseClassName: string) => {
		const hasError = hasFieldError(fieldName);
		return `${baseClassName} ${hasError ? "border-red-500 focus:border-red-500" : "border-gray-300 focus:border-blue-500"}`;
	};
	return (
		<section id="personal-details-section">
			<h2 className="text-lg mt-8">Personal Details</h2>
			<div className="flex flex-col validate" style={{ gap: "24px" }}>
				<div>
					<label htmlFor="personal-details-section--first-name">
						First name
					</label>
					<input
						type="text"
						id="personal-details-section--first-name"
						name="first_name"
						className={getInputClassName(
							"firstName",
							"block w-full p-2 text-md border rounded-full focus-outline px-4 mt-1",
						)}
						value={firstName}
						onChange={(e) =>
							handleFieldChange("firstName", e.target.value, onFirstNameChange)
						}
						onBlur={() => handleFieldBlur("firstName")}
						maxLength={100}
						required
						aria-invalid={hasFieldError("firstName")}
						aria-describedby={
							hasFieldError("firstName") ? "firstName-error" : undefined
						}
					/>
					{getFieldError("firstName") && (
						<p id="firstName-error" className="text-sm mt-1" role="alert">
							{getFieldError("firstName")}
						</p>
					)}
				</div>

				<div>
					<label htmlFor="personal-details-section--last-name">Last name</label>
					<input
						type="text"
						id="personal-details-section--last-name"
						name="last_name"
						className={getInputClassName(
							"lastName",
							"block w-full p-2 text-md border rounded-full focus-outline px-4 mt-1",
						)}
						value={lastName}
						onChange={(e) =>
							handleFieldChange("lastName", e.target.value, onLastNameChange)
						}
						onBlur={() => handleFieldBlur("lastName")}
						maxLength={100}
						required
						aria-invalid={hasFieldError("lastName")}
						aria-describedby={
							hasFieldError("lastName") ? "lastName-error" : undefined
						}
					/>
					{getFieldError("lastName") && (
						<p id="lastName-error" className="text-sm mt-1" role="alert">
							{getFieldError("lastName")}
						</p>
					)}
				</div>

				<div>
					<label htmlFor="personal-details-section--email">Email</label>
					<input
						id="personal-details-section--email"
						name="email"
						type="email"
						className={getInputClassName(
							"email",
							"block w-full p-2 text-md border rounded-full focus-outline px-4 mt-1",
						)}
						value={email}
						onChange={(e) =>
							handleFieldChange("email", e.target.value, onEmailChange)
						}
						onBlur={() => handleFieldBlur("email")}
						maxLength={254}
						required
						aria-invalid={hasFieldError("email")}
						aria-describedby={
							hasFieldError("email") ? "email-error" : undefined
						}
					/>
					{getFieldError("email") && (
						<p id="email-error" className="text-sm mt-1" role="alert">
							{getFieldError("email")}
						</p>
					)}
				</div>

				<div className="flex flex-col">
					<label htmlFor="personal-details-section--country">Country</label>
					<select
						name="country"
						id="personal-details-section--country"
						className={getInputClassName(
							"country",
							"gw-select text-md flex flex-grow rounded-full border h-12 border-none p-2 focus-outline mt-1 px-4",
						)}
						value={country}
						onChange={(e) => handleCountryChange(e.target.value as Country)}
						onBlur={() => handleFieldBlur("country")}
						required
						aria-invalid={hasFieldError("country")}
						aria-describedby={
							hasFieldError("country") ? "country-error" : undefined
						}
					>
						{COUNTRY_LIST.map((countryOption) => (
							<option key={countryOption} value={countryOption}>
								{countryOption}
							</option>
						))}
					</select>
					{getFieldError("country") && (
						<p id="country-error" className="text-sm mt-1" role="alert">
							{getFieldError("country")}
						</p>
					)}
				</div>
			</div>
		</section>
	);
};
